// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import {
    FeedbackAuthenticationType,
    FeedbackHostPlatformType,
    FeedbackUiType,
    IFeedbackConfig,
    IFeedbackInitOptions,
    InAppFeedbackScenarioType,
    FeedbackAgeGroup,
    FeedbackScreenshotSourceType,
    FeedbackScreenshotInfoImageFormat,
    IFeedbackTelemetry,
} from '@ms/inapp-feedback-loader';
import Bowser from 'bowser';
import { traceEvent, trackError, trackInfo } from '../../utilities';
import { ProfileTheme } from '@microsoft/vscodeedu-common';

export function createFeedbackInitOptions(theme: ProfileTheme, appId: number, isProd: boolean): IFeedbackInitOptions {
    const telemetry: IFeedbackTelemetry = {
        browser: 'unknown',
        browserVersion: 'unknown',
        deviceType: 'unknown',
        platform: 'unknown',
        sourcePageName: 'unknown',
    };

    try {
        const bowser = Bowser.getParser(window.navigator.userAgent);
        telemetry.browser = bowser.getBrowserName() || telemetry.browser;
        telemetry.browserVersion = bowser.getBrowserVersion() || telemetry.browserVersion;
        telemetry.deviceType = bowser.getPlatformType() || telemetry.deviceType;
        telemetry.platform = bowser.getOSName() || telemetry.platform;
        telemetry.sourcePageName = window.location.href;
    } catch (e) {
        trackError(e);
    }

    return {
        authenticationType: FeedbackAuthenticationType.Unauthenticated,
        ageGroup: FeedbackAgeGroup.NotAdult,
        isProduction: isProd,
        appId: appId,
        callbackFunctions: {
            onDismiss: () => {
                onDismiss();
            },
            onSuccess: onSuccess,
            onError: onError,
            initializationComplete,
        },
        feedbackConfig: {
            ...baseFeedbackConfig,
            appData: JSON.stringify({ commit: process.env.REACT_APP_VERSION }),
        },
        themeOptions: {
            baseTheme: getBaseOCVTheme(theme),
        },
        telemetry,
    };
}

function getBaseOCVTheme(theme: ProfileTheme): string {
    switch (theme) {
        case 'light':
        case 'hc-light':
            return 'M365Light';
        case 'dark':
        case 'hc-dark':
            return 'M365Dark';
        default:
            return 'M365Dark';
    }
}

function onDismiss(isFeedbackSent?: boolean) {
    traceEvent('InAppFeedbackDismiss', {
        url: window.location.href,
        isFeedbackSent,
    });
}

function onSuccess() {
    traceEvent('InAppFeedbackSent', {
        url: window.location.href,
    });
}

function onError(errorMessage?: string) {
    trackError(new Error(errorMessage ?? 'Unknown error'));
}

function initializationComplete() {
    trackInfo('Feedback initialization complete');
}

const baseFeedbackConfig: IFeedbackConfig = {
    email: '',
    feedbackForumUrl: 'https://community.vscodeedu.com',
    feedbackInsightsConfig: {
        isFeedbackInsightsEnabled: false,
    },
    feedbackUiType: FeedbackUiType.Modal,
    hostPlatform: FeedbackHostPlatformType.IFrame,
    isDisplayed: true,
    isEmailCollectionEnabled: true,
    isFeedbackForumEnabled: false,
    isFileUploadEnabled: true,
    isMyFeedbackEnabled: false,
    invokeOnDismissOnEsc: true,
    isScreenshotEnabled: true,
    isThankYouPageDisabled: false,
    myFeedbackUrl: 'https://feedbackportal.microsoft.com/feedback/mycontent/',
    privacyUrl: 'https://go.microsoft.com/fwlink/?LinkId=521839',
    retentionDurationDays: 30,
    screenshotInfo: {
        providedScreenshotType: FeedbackScreenshotSourceType.AutoCaptureWithHtml2Canvas,
        screenshotImageFormat: FeedbackScreenshotInfoImageFormat.PNG,
    },
    msInternalTitleTarget: 'VSCode Edu',
    isSupportEnabled: false,
    displayAdditionalInformationEnabled: false,

    scenarioConfig: {
        scenarioType: InAppFeedbackScenarioType.Experience,
        isScenarioEnabled: true,
    },

    tenantFeedbackServiceOptions: {
        useTenantFeedbackService: false,
    },
};
